package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.budget.GetMissingPayUseCase
import com.evoapps.evoshift.domain.budget.MissingPay
import com.evoapps.evoshift.domain.budget.PayTermsChanges
import com.evoapps.evoshift.domain.budget.PersonWithoutPay
import com.evoapps.evoshift.domain.budget.SetWorksUnpaidUseCase
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.capabilitiesAt
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.billing.ObservePlanActiveUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable

/** SH-147: where the missing-pay warning looks: a roster month, or one [date] ("YYYY-MM-DD") of it. */
@Serializable
data class MissingPayScope(val rosterId: String, val date: String? = null) {
    val workplaceId: String get() = RosterIds.workplaceIdOf(rosterId)
}

data class MissingPayUiState(
    val missing: MissingPay? = null,
    /** What the person may do at the workplace: the warning needs VIEW_FINANCES (money roles). */
    val capabilities: Set<Capability> = emptySet(),
    /** Who is signed in ("That's me" for their own entry). */
    val currentUserId: String? = null,
    /** Confirmations on their way (their buttons are disabled). */
    val confirming: Set<String> = emptySet(),
    /** Why the last confirmation failed (shown in the warning until the next one). */
    val error: UiText? = null
) {
    val people: List<PersonWithoutPay> get() = missing?.people.orEmpty()

    /** The warning shows: money roles, someone missing. */
    val shows: Boolean get() = capabilities.shows(Gate.MONEY) && (people.isNotEmpty() || error != null)

    /** "Set pay": those who set pay terms (owners, financial managers), while the plan runs. */
    val canSetPay: Boolean get() = capabilities.shows(Gate.PAY_EDITING)

    /** "Works without pay": the workplace's payroll settings, the same people. */
    val canConfirmUnpaid: Boolean get() = capabilities.shows(Gate.WORKPLACE_FINANCES_EDITING)
}

/**
 * SH-147: the "N people without pay set: labour cost is understated" warning of one screen (the
 * month's or a day's): who works without pay terms, "Set pay" (the screen opens the person's pay
 * terms) and "Works without pay (0 zł)", stored once. Read again whenever pay terms change
 * ([PayTermsChanges]), so the warning goes once the pay is set.
 */
class MissingPayViewModel(
    private val scope: MissingPayScope,
    private val getMissingPay: GetMissingPayUseCase,
    private val getCurrentUser: GetCurrentUserUseCase,
    private val setWorksUnpaid: SetWorksUnpaidUseCase? = null,
    private val observePlanActive: ObservePlanActiveUseCase? = null,
    private val payTermsChanges: PayTermsChanges? = null
) : ViewModel() {
    private val _uiState = MutableStateFlow(MissingPayUiState())
    val uiState = _uiState.asStateFlow()

    init {
        viewModelScope.launch { load() }
        payTermsChanges?.let { changes ->
            viewModelScope.launch { changes.version.drop(1).collect { load() } }
        }
    }

    /** Reads the warning again (quietly: what is shown stays until the answer). */
    fun refresh() {
        viewModelScope.launch { load() }
    }

    private suspend fun load() {
        val user = getCurrentUser().first()
        val workplaceId = scope.workplaceId
        val capabilities = user?.capabilitiesAt(workplaceId, observePlanActive.activeNow(workplaceId)).orEmpty()
        _uiState.update { it.copy(capabilities = capabilities, currentUserId = user?.uid) }
        // Money only: nothing is read for anyone else.
        if (!capabilities.shows(Gate.MONEY)) {
            _uiState.update { it.copy(missing = null) }
            return
        }
        // A failed read shows no warning rather than a wrong one; the next change reads again.
        getMissingPay(scope.rosterId, scope.date).onSuccess { missing ->
            _uiState.update { state -> state.copy(missing = missing.copy(people = missing.people.filterNot { it.userId in state.confirming })) }
        }
    }

    /**
     * [userId] works without pay (0 zł): stored once in the workplace's payroll settings, so they
     * never count as missing pay again. Optimistic: they leave the warning at once and come back
     * with the reason when the save fails.
     */
    fun confirmWorksUnpaid(userId: String) {
        val useCase = setWorksUnpaid ?: return
        val before = _uiState.value
        if (!before.canConfirmUnpaid || userId in before.confirming) return
        val missing = before.missing ?: return
        _uiState.update {
            it.copy(
                missing = missing.copy(people = missing.people.filterNot { p -> p.userId == userId }),
                confirming = it.confirming + userId,
                error = null
            )
        }
        viewModelScope.launch {
            useCase(scope.workplaceId, userId, unpaid = true).fold(
                // The person leaving the warning is the confirmation.
                onSuccess = { _uiState.update { it.copy(confirming = it.confirming - userId) } },
                onFailure = { e ->
                    _uiState.update { it.copy(missing = before.missing, confirming = it.confirming - userId, error = e.toUiText()) }
                }
            )
        }
    }
}
